<template>
  <teleport v-if="isOpen" to="#th-modals" :disabled="showInline || debug">
    <div class="th-modal-wrapper" :data-testid="dataTestid">
      <transition name="fade" :css="!debug">
        <div
          v-show="isShown"
          class="th-modal-overlay"
          data-testid="th-modal-overlay"
          @click="handleOverlayClick"
        />
      </transition>
      <transition name="flyin" :css="!debug">
        <div
          v-show="isShown"
          :class="`th-modal shadow-md ${modalClass}`"
          :style="{ width, height }"
        >
          <div
            v-loading="loading"
            :element-loading-text="loadingText"
            class="th-modal__content"
          >
            <!-- Header -->
            <div
              v-if="$slots.title || title || $slots.subtitle || subtitle"
              class="th-modal-header leading-normal"
            >
              <div class="grow text-lg">
                <slot v-if="$slots.title || title" name="title">
                  {{ title }}
                </slot>
                <div class="text-xs" :class="{ italic: !isWhiteLabel }">
                  <slot v-if="$slots.subtitle || subtitle" name="subtitle">
                    {{ subtitle }}
                  </slot>
                </div>
              </div>

              <!-- Close -->
              <el-button
                v-if="showCloseButton"
                :data-testid="closeButtonTestId"
                class="text-current text-lg p-0 h-5 w-5 ml-2 shrink-0"
                :icon="CloseIcon"
                text
                @click="handleClose"
              />
            </div>

            <!-- Body -->
            <div class="th-modal-body" :class="bodyClass">
              <slot />
            </div>

            <!-- Footer -->
            <div v-if="$slots.footer" class="th-modal-footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script>
import { isUnifiedCommerce } from '@shared/constants'
import { CloseIcon } from '@shared/assets/icons'

export default {
  name: 'ThModal',

  props: {
    name: {
      type: String,
      required: true
    },
    height: {
      type: String,
      default: ''
    },
    width: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      required: false,
      default: undefined
    },
    subtitle: {
      type: String,
      required: false,
      default: undefined
    },
    modalClass: {
      type: String,
      required: false,
      default: null
    },
    bodyClass: {
      type: String,
      required: false,
      default: null
    },
    showInline: {
      // Use teleport to move modal to body or keep it inline
      type: Boolean,
      required: false,
      default: false
    },
    dataTestid: {
      type: String,
      required: false,
      default: undefined
    },
    closeButtonTestId: {
      type: String,
      required: false,
      default: 'th-modal-close_button'
    },
    showCloseButton: {
      type: Boolean,
      required: false,
      default: true
    },
    closeOnClickOutside: {
      type: Boolean,
      required: false,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: ''
    },
    beforeClose: {
      type: Function,
      default: null
    }
  },

  emits: ['close'],

  data() {
    return {
      debug: false,
      isOpen: false,
      isShown: false,
      CloseIcon
    }
  },

  computed: {
    isWhiteLabel() {
      return isUnifiedCommerce()
    }
  },

  beforeMount() {
    // Always open if debug is true
    this.debug = this.$thModal.debug
    if (this.debug) {
      this.isOpen = true
      this.isShown = true
    }
  },

  mounted() {
    this.$thModal.listen(this.onToggle)
  },

  methods: {
    handleOverlayClick() {
      if (!this.closeOnClickOutside) return
      this.handleClose()
    },

    async handleClose() {
      if (this.beforeClose && !(await this.beforeClose())) {
        return
      }
      this.$emit('close')
      this.$thModal.hide(this.name)
    },

    onToggle(name, state) {
      if (name === this.name) {
        const newState = state == null ? !this.isOpen : state
        if (newState) {
          this.isOpen = true
          this.$nextTick(() => {
            this.isShown = true
          })
        } else {
          this.isShown = false
          setTimeout(() => {
            this.isOpen = false
          }, 300)
        }
      }
    }
  }
}
</script>

<style scoped>
.th-modal-wrapper {
  --th-modal-padding: 2rem;

  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100vh;
  z-index: 999;

  display: flex;
  align-items: center;
  justify-content: center;
}

.th-modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  min-height: 100%;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.th-modal {
  display: flex;
  flex-direction: column;
  background: var(--background-color);
  border-radius: var(--border-radius);
  max-width: 90%;
  max-height: 90%;
  z-index: 2;
}

.th-modal__content {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
}

.th-modal-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: var(--th-modal-padding);
  padding-bottom: 0;
}

.th-modal-body {
  overflow: auto;
  flex-grow: 1;
  padding: 0 var(--th-modal-padding);
  margin: var(--th-modal-padding) 0;
  color: var(--text-default-color);
}

.th-modal-footer {
  flex-shrink: 0;
  padding: var(--th-modal-padding);
  padding-top: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.flyin-enter-active,
.flyin-leave-active {
  transition: all 0.3s;
}
.flyin-enter,
.flyin-leave-to {
  opacity: 0;
  /* transform: scale(0.9) translateY(-1rem) translateZ(0) rotateX(90deg); */
  transform: scale(0.9, 0.5) translateY(-1rem);
}
</style>
